import { useMemo, useState } from "react";

import { toBcp47 } from "@/i18n";
import type { LeaseCalculatorResult } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useLocale, useT } from "@/locale-context";

const ALLOWED_TERMS = [24, 36, 48, 60] as const;
const FINANCING_PREMIUM = 0.03;
const SLIDER_STEP = 500;

export function LeaseCalculatorCard({ data }: { data: LeaseCalculatorResult }) {
  const t = useT();
  const locale = useLocale();

  const priceFormatter = useMemo(
    () =>
      new Intl.NumberFormat(toBcp47(locale), {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }),
    [locale],
  );

  const seededTerm = ALLOWED_TERMS.includes(
    data.termMonths as (typeof ALLOWED_TERMS)[number],
  )
    ? (data.termMonths as (typeof ALLOWED_TERMS)[number])
    : 36;
  const [termMonths, setTermMonths] =
    useState<(typeof ALLOWED_TERMS)[number]>(seededTerm);

  const clampedSeedDp = Math.max(0, Math.min(data.priceEur, data.downPaymentEur));
  const [downPaymentEur, setDownPaymentEur] = useState<number>(clampedSeedDp);

  const monthly = useMemo(() => {
    if (data.priceEur <= 0) return 0;
    const raw =
      ((data.priceEur - downPaymentEur) / termMonths) * (1 + FINANCING_PREMIUM);
    return Math.round(raw);
  }, [termMonths, downPaymentEur, data.priceEur]);

  return (
    <div className="my-3 overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
      <div className="space-y-4 p-4">
        <p className="font-medium text-neutral-700 text-xs">
          {t("leaseCalc.label")}
        </p>

        <div>
          <p className="mb-1.5 font-medium text-neutral-700 text-xs">
            {t("leaseCalc.term")}
          </p>
          <div className="grid grid-cols-4 gap-2">
            {ALLOWED_TERMS.map((term) => {
              const selected = term === termMonths;
              return (
                <button
                  key={term}
                  type="button"
                  onClick={() => setTermMonths(term)}
                  className={cn(
                    "rounded-full border px-3 py-1.5 text-xs transition",
                    selected
                      ? "border-blue-500 bg-white text-blue-700 shadow-sm"
                      : "border-neutral-200 bg-white text-neutral-700 hover:border-blue-300",
                  )}
                >
                  {term}mo
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className="mb-1.5 font-medium text-neutral-700 text-xs">
            {t("leaseCalc.downPaymentLabel", {
              amount: priceFormatter.format(downPaymentEur),
            })}
          </p>
          <input
            type="range"
            min={0}
            max={data.priceEur}
            step={SLIDER_STEP}
            value={downPaymentEur}
            onChange={(event) => setDownPaymentEur(Number(event.target.value))}
            className="h-2 w-full cursor-pointer appearance-none rounded-full bg-neutral-200 accent-blue-500"
          />
        </div>

        <div className="rounded-xl border border-blue-100 bg-gradient-to-br from-sky-50 to-blue-50 p-4 text-center">
          <p className="text-neutral-600 text-xs">{t("leaseCalc.estimated")}</p>
          <p className="mt-1 flex items-baseline justify-center">
            <span className="font-bold text-3xl text-blue-600">
              {priceFormatter.format(monthly)}
            </span>
            <span className="ml-1 align-baseline text-neutral-500 text-sm">
              {t("leaseCalc.perMonth")}
            </span>
          </p>
          {data.fuelSavingsPerYearEur ? (
            <p className="mt-1 text-[11px] text-neutral-600">
              {t("leaseCalc.fuelSavings", {
                amount: priceFormatter.format(data.fuelSavingsPerYearEur),
              })}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
